import React, { useEffect, useMemo, useState } from 'react';
import { Ticket, TransferLogEntry } from '../types';
import { Button, Card, Flex, Input, Text } from './base-components';
import { useToast } from '../contexts/ToastContext';
import { useBatchTransfer, useCheckDuplicateTransfer } from '../hooks/useApi';
import { trackEvent } from '../services/analytics';
import {
  BatchTransferPlatform,
  isTransferableOn,
  accountLabel,
  ticketTypeLabel,
  ticketShortId,
  normalizePhone,
  isPhoneValid,
} from './transferUtils';

interface PerAccountResult {
  connection_id: number | null;
  account_label: string;
  ticket_ids: string[];
  success: boolean;
  tickets_transferred?: number;
  error?: string;
  error_type?: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  eventName: string;
  eventDateLabel: string;
  venueLabel: string;
  externalEventId: string;
  tickets: Ticket[];
  platform: BatchTransferPlatform;
}


export const EventTransferModal: React.FC<Props> = ({
  open,
  onClose,
  eventName,
  eventDateLabel,
  venueLabel,
  externalEventId,
  tickets,
  platform,
}) => {
  const { showSuccess, showError } = useToast();
  const batch = useBatchTransfer();
  const dupCheck = useCheckDuplicateTransfer();
  const [strictMatches, setStrictMatches] = useState<TransferLogEntry[] | null>(null);
  const [looseMatches, setLooseMatches] = useState<TransferLogEntry[]>([]);
  const [confirmAnyway, setConfirmAnyway] = useState(false);

  const transferable = useMemo(
    () => tickets.filter((t) => isTransferableOn(t, platform)),
    [tickets, platform],
  );

  const accountGroups = useMemo(() => {
    const groups = new Map<string, { key: string; connectionId: number | null; label: string; tickets: Ticket[] }>();
    transferable.forEach((t) => {
      const conn = t.external_platform_connection;
      const key = conn ? `c${conn.id}` : 'none';
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          connectionId: conn?.id ?? null,
          label: accountLabel(t),
          tickets: [],
        });
      }
      groups.get(key)!.tickets.push(t);
    });
    return Array.from(groups.values()).sort((a, b) => b.tickets.length - a.tickets.length);
  }, [transferable]);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [phone, setPhone] = useState('');
  const [results, setResults] = useState<PerAccountResult[] | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedIds(new Set());
      setPhone('');
      setResults(null);
      setStrictMatches(null);
      setLooseMatches([]);
      setConfirmAnyway(false);
    }
  }, [open]);

  // Reset confirmation when the user changes their selection or recipient.
  useEffect(() => {
    setStrictMatches(null);
    setLooseMatches([]);
    setConfirmAnyway(false);
  }, [phone, selectedIds]);

  const totalSelected = selectedIds.size;
  const phoneInvalid = phone.length > 0 && !isPhoneValid(phone);

  const selectionByGroup = useMemo(() => {
    const counts: Record<string, number> = {};
    accountGroups.forEach((g) => {
      counts[g.key] = g.tickets.filter((t) => selectedIds.has(t.external_ticket_id)).length;
    });
    return counts;
  }, [accountGroups, selectedIds]);

  const toggleTicket = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleGroup = (groupKey: string) => {
    const group = accountGroups.find((g) => g.key === groupKey);
    if (!group) return;
    const allSelected = group.tickets.every((t) => selectedIds.has(t.external_ticket_id));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      group.tickets.forEach((t) => {
        if (allSelected) next.delete(t.external_ticket_id);
        else next.add(t.external_ticket_id);
      });
      return next;
    });
  };

  const canSubmit =
    !batch.isPending &&
    totalSelected > 0 &&
    isPhoneValid(phone);

  const handleSubmit = async (bypassDupCheck = false) => {
    if (!canSubmit) return;

    const transfersByConnection = accountGroups
      .map((g) => {
        const ids = g.tickets
          .filter((t) => selectedIds.has(t.external_ticket_id))
          .map((t) => t.external_ticket_id);
        return { group: g, ids };
      })
      .filter((x) => x.ids.length > 0);

    const transfers = transfersByConnection.map(({ group, ids }) => ({
      event_id: externalEventId,
      recipient_phone: normalizePhone(phone),
      ticket_ids: ids,
      client_ref: group.key,
    }));

    // Pre-submit duplicate guard. Skip if the user already confirmed past the warning.
    if (!bypassDupCheck && !confirmAnyway) {
      const allTicketIds = transfers.flatMap((t) => t.ticket_ids);
      try {
        const dup = await dupCheck.mutateAsync({
          event_id: externalEventId,
          recipient_phone: normalizePhone(phone),
          ticket_ids: allTicketIds,
        });
        if (dup.strict_matches.length > 0 || dup.loose_matches.length > 0) {
          setStrictMatches(dup.strict_matches);
          setLooseMatches(dup.loose_matches);
          // Block until user clicks "Send anyway" if any STRICT match.
          if (dup.strict_matches.length > 0) return;
        }
      } catch {
        // Soft-fail: if the check itself errors, fall through and submit.
      }
    }

    try {
      trackEvent('recipient_transfer_submitted', {
        accountCount: transfers.length,
        ticketCount: totalSelected,
        eventId: externalEventId,
      });
      const res = await batch.mutateAsync({ platform, transfers });

      const perAccount: PerAccountResult[] = res.results.map((r) => {
        const matchingGroup = transfersByConnection.find((x) => x.group.key === r.client_ref)?.group;
        return {
          connection_id: matchingGroup?.connectionId ?? null,
          account_label: matchingGroup?.label ?? 'Account',
          ticket_ids: r.ticket_ids,
          success: r.success,
          tickets_transferred: r.tickets_transferred,
          error: r.error,
          error_type: r.error_type,
        };
      });
      setResults(perAccount);

      const { succeeded, failed, tickets_transferred } = res.summary;
      if (failed === 0) {
        showSuccess(`Transferred ${tickets_transferred} ticket${tickets_transferred === 1 ? '' : 's'}`);
      } else if (succeeded === 0) {
        showError('Transfer failed');
      } else {
        showError(`Sent ${tickets_transferred}, ${failed} account${failed === 1 ? '' : 's'} failed`);
      }
    } catch (e: any) {
      showError(e?.message || 'Transfer failed');
    }
  };

  if (!open) return null;

  const allSucceeded =
    results !== null && results.length > 0 && results.every((r) => r.success);

  return (
    <Flex
      align="center"
      justify="center"
      fixed
      inset
      bg="black/80"
      backdrop
      zIndex={1000}
      onClick={onClose}
    >
      <Card
        fullWidth
        maxWidth="lg"
        shadow
        animate="slideUp"
        bg="dark"
        padding="lg"
        hover={false}
        onClick={(e) => e.stopPropagation()}
      >
        <Text variant="heading" mb={1}>Transfer tickets</Text>
        <Text variant="muted" size="sm" mb={1}>{eventName}</Text>
        <Text variant="muted" size="xs" mb={4}>
          {eventDateLabel}{venueLabel ? ` · ${venueLabel}` : ''}
        </Text>

        {transferable.length === 0 && !results && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <p className="text-amber-400 text-sm">
              No transferable {platform} tickets in this event group.
            </p>
          </div>
        )}

        {!results && transferable.length > 0 && (
          <>
            <div className="mb-3 flex items-center justify-between">
              <span className="text-sm text-white/60">
                {totalSelected > 0 ? (
                  <>
                    <span className="text-white font-medium">{totalSelected}</span> selected
                    {accountGroups.length > 1 && (
                      <span className="text-white/40">
                        {' '}·{' '}
                        {accountGroups
                          .filter((g) => selectionByGroup[g.key] > 0)
                          .map((g) => `${selectionByGroup[g.key]} from ${g.label}`)
                          .join(' · ')}
                      </span>
                    )}
                  </>
                ) : (
                  <span className="text-white/40">Select tickets to transfer</span>
                )}
              </span>
              <span className="text-xs text-white/30">
                {transferable.length} available across {accountGroups.length} account{accountGroups.length === 1 ? '' : 's'}
              </span>
            </div>

            <div className="space-y-3 mb-4 max-h-[40vh] overflow-y-auto pr-1">
              {accountGroups.map((group) => {
                const selectedInGroup = selectionByGroup[group.key];
                const allInGroupSelected = selectedInGroup === group.tickets.length;
                return (
                  <div
                    key={group.key}
                    className="rounded-lg bg-white/[0.02] border border-white/[0.06] overflow-hidden"
                  >
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.key)}
                      className="w-full flex items-center justify-between px-3 py-2 bg-white/[0.03] hover:bg-white/[0.05] transition-colors"
                    >
                      <span className="flex items-center gap-2">
                        <span className={`w-4 h-4 rounded border flex items-center justify-center ${
                          allInGroupSelected
                            ? 'bg-blue-500 border-blue-500'
                            : selectedInGroup > 0
                              ? 'bg-blue-500/30 border-blue-500/60'
                              : 'border-white/20'
                        }`}>
                          {allInGroupSelected && (
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                          )}
                          {!allInGroupSelected && selectedInGroup > 0 && (
                            <span className="w-2 h-0.5 bg-white rounded-full" />
                          )}
                        </span>
                        <span className="text-sm text-white font-medium">{group.label}</span>
                        <span className="text-xs text-white/40">
                          {selectedInGroup}/{group.tickets.length}
                        </span>
                      </span>
                    </button>
                    <div className="divide-y divide-white/[0.04]">
                      {group.tickets.map((t, i) => {
                        const checked = selectedIds.has(t.external_ticket_id);
                        return (
                          <label
                            key={t.external_ticket_id}
                            className={`flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors ${
                              checked ? 'bg-blue-500/[0.06]' : 'hover:bg-white/[0.02]'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleTicket(t.external_ticket_id)}
                              className="w-4 h-4 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500/50 focus:ring-offset-0"
                            />
                            <span className="text-xs text-white/40 tabular-nums w-6">#{i + 1}</span>
                            <span className="text-sm text-white/80">{ticketTypeLabel(t)}</span>
                            <span className="text-xs text-white/30 ml-auto font-mono">
                              {ticketShortId(t)}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mb-4">
              <Input
                type="tel"
                label="Recipient phone (with country code)"
                placeholder="+15551234567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                error={phoneInvalid}
                errorMessage={phoneInvalid ? 'e.g. +15551234567' : undefined}
              />
            </div>

            {strictMatches && strictMatches.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.732-3L13.732 4a2 2 0 00-3.464 0L3.268 16A2 2 0 005 19z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-red-400">
                      You already sent {strictMatches.length === 1 ? 'this' : 'some of these'} ticket{strictMatches.length === 1 && (strictMatches[0].overlapping_ticket_ids?.length || 0) <= 1 ? '' : 's'} to this number.
                    </p>
                    <ul className="mt-1 space-y-0.5">
                      {strictMatches.slice(0, 3).map((m) => (
                        <li key={m.id} className="text-xs text-red-300/80">
                          {(m.overlapping_ticket_ids?.length || 0)} ticket{(m.overlapping_ticket_ids?.length || 0) === 1 ? '' : 's'}
                          {' '}sent {new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          {m.account?.phone_masked && ` from ${m.account.phone_masked}`}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {looseMatches.length > 0 && (
              <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-xs text-amber-400">
                  You sent <span className="font-medium">{looseMatches.length}</span> other transfer{looseMatches.length === 1 ? '' : 's'} for this event to this number recently. Check before sending again.
                </p>
              </div>
            )}

            <Flex justify="end" gap={2}>
              <Button variant="secondary" onClick={onClose} disabled={batch.isPending || dupCheck.isPending}>
                Cancel
              </Button>
              {strictMatches && strictMatches.length > 0 && !confirmAnyway ? (
                <Button
                  variant="primary"
                  onClick={() => { setConfirmAnyway(true); handleSubmit(true); }}
                  disabled={batch.isPending || dupCheck.isPending}
                >
                  Send anyway
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onClick={() => handleSubmit()}
                  disabled={!canSubmit || dupCheck.isPending}
                >
                  {batch.isPending
                    ? 'Sending…'
                    : dupCheck.isPending
                      ? 'Checking…'
                      : `Send ${totalSelected} ticket${totalSelected === 1 ? '' : 's'}`}
                </Button>
              )}
            </Flex>
          </>
        )}

        {results && (
          <>
            <div className="space-y-2 mb-4 max-h-[50vh] overflow-y-auto pr-1">
              {results.map((r, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    r.success
                      ? 'bg-emerald-500/10 border-emerald-500/30'
                      : 'bg-red-500/10 border-red-500/30'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-white">{r.account_label}</span>
                    <span className={`text-xs font-medium ${r.success ? 'text-emerald-400' : 'text-red-400'}`}>
                      {r.success
                        ? `${r.tickets_transferred ?? r.ticket_ids.length} sent`
                        : 'Failed'}
                    </span>
                  </div>
                  {!r.success && r.error && (
                    <p className="text-xs text-red-400/90 mt-1">{r.error}</p>
                  )}
                </div>
              ))}
            </div>
            <Flex justify="end" gap={2}>
              <Button variant="primary" onClick={onClose}>
                {allSucceeded ? 'Done' : 'Close'}
              </Button>
            </Flex>
          </>
        )}
      </Card>
    </Flex>
  );
};
